import React, { Component } from "react";
import EntityDropdown from "../../component/EntityDropdown";
import { fetchList, getDeactivation, isInactive } from "./useResource";
import { POINTS_ADMIN } from "../../config/lang";
import { translate } from "../../util/util";
import { FILTER_DEBOUNCE_MS } from "../../config/config";

const PAGE_SIZE = 25;

export default class ResourceTable extends Component {
  state = { rows: [], count: 0, page: 1, search: "", filters: {}, loading: false, checked: [], ordering: this.props.descriptor.defaultOrdering || "name" };
  _debounce = null;

  componentDidMount() { this._isMounted = true; this.load(); }

  componentWillUnmount() { this._isMounted = false; clearTimeout(this._debounce); }

  load = () => {
    const { descriptor } = this.props;
    const { page, search, filters, ordering } = this.state;
    this.setState({ loading: true });
    fetchList(descriptor, { page, search, filters, ordering }, ({ results, count }) => {
      if (!this._isMounted) return;
      this.setState({ rows: results, count, loading: false });
    });
  };

  // Cycle a column's ordering: asc -> desc -> cleared (reverts to the API default).
  toggleSort = key => {
    this.setState(s => {
      let ordering;
      if (s.ordering === key) ordering = "-" + key;
      else if (s.ordering === "-" + key) ordering = "";
      else ordering = key;
      return { ordering, page: 1 };
    }, this.load);
  };

  onSearch = e => {
    const search = e.target.value;
    this.setState({ search, page: 1 });
    clearTimeout(this._debounce);
    this._debounce = setTimeout(this.load, FILTER_DEBOUNCE_MS);
  };

  setFilter = (name, option) => {
    this.setState(s => {
      const filters = { ...s.filters, [name]: option ? option.id : "" };
      (this.props.descriptor.filters || []).forEach(f => {
        if (f.scopeBy === name) filters[f.name] = "";
      });
      return { filters, page: 1 };
    }, this.load);
  };

  goPage = page => this.setState({ page }, this.load);

  toggleCheck = (row) => this.setState(s => ({
    checked: s.checked.some(c => c.id === row.id)
      ? s.checked.filter(c => c.id !== row.id)
      : [...s.checked, { id: row.id, name: row.name }],
  }));

  clearChecked = () => { if (this._isMounted) this.setState({ checked: [] }); };

  render() {
    const { descriptor, currentUser, onOpen } = this.props;
    const { rows, count, page, search, loading, checked, ordering } = this.state;
    const G = POINTS_ADMIN, lang = currentUser.language;
    const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));
    const hasBulk = Array.isArray(descriptor.bulkActions) && descriptor.bulkActions.length > 0;
    const deact = getDeactivation(descriptor);

    return (
      <div className="pt-table-wrap">
        <div className="pt-toolbar">
          <div className="pt-search">
            <span className="material-symbols-outlined">search</span>
            <input className="mp-form-input" value={search} placeholder={translate(G, "search_ph", lang)} onChange={this.onSearch} />
          </div>
          {(descriptor.filters || []).map(f =>
            f.type === "me" ? (
              <label className="pt-filter-check" key={f.name}>
                <input
                  type="checkbox"
                  checked={!!this.state.filters[f.name]}
                  onChange={e => this.setFilter(f.name, e.target.checked ? { id: currentUser.id } : null)}
                />
                <span>{translate(G, f.label, lang)}</span>
              </label>
            ) : (
              <div className="pt-filter" key={f.name}>
                <EntityDropdown
                  entity={f.entity}
                  value={this.state.filters[f.name] || ""}
                  county={f.scopeBy ? this.state.filters[f.scopeBy] : undefined}
                  placeholder={f.label ? translate(G, f.label, lang) : undefined}
                  onChange={o => this.setFilter(f.name, o)}
                />
              </div>
            )
          )}
          {!descriptor.readOnly && (
            <button className="mp-save-btn pt-new" onClick={() => onOpen("new")}>
              <span className="material-symbols-outlined">add</span>{translate(G, "new_btn", lang)}
            </button>
          )}
        </div>

        {hasBulk && (
          <div className="pt-bulk-actions">
            {descriptor.bulkActions.map(a => (
              <a.Component key={a.key} checked={checked} rows={rows} currentUser={currentUser} onDone={() => { this.clearChecked(); this.load(); }} />
            ))}
          </div>
        )}

        <div className="pt-table-area">
          {loading && (
            <div className="pt-loading-overlay">
              <i className="fa fa-spinner fa-pulse fa-2x fa-fw" />
            </div>
          )}
          <table className="table table-hover">
          <thead>
            <tr>
              {hasBulk && <th className="pt-check-col" />}
              {descriptor.columns.map((c, idx) => {
                const key = c.sort || (c.field === "name" ? "name" : null);
                const arrow = key && ordering === key ? " ↑" : key && ordering === "-" + key ? " ↓" : "";
                return (
                  <th
                    key={c.field || c.label || idx}
                    className={key ? "pt-sortable" : undefined}
                    onClick={key ? () => this.toggleSort(key) : undefined}>
                    {translate(G, c.label, lang) || c.label}
                    {key && <span className="pt-sort-arrow">{arrow}</span>}
                  </th>
                );
              })}
              {deact && <th>{translate(G, "f_status", lang)}</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} className={"merch-clickable-row" + (deact && isInactive(row, deact) ? " pt-row-removed" : "")} onClick={() => onOpen(row.id)}>
                {hasBulk && (
                  <td className="pt-check-col" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={checked.some(c => c.id === row.id)} onChange={() => this.toggleCheck(row)} />
                  </td>
                )}
                {descriptor.columns.map((c, idx) => (
                  <td key={c.field || c.label || idx}>{c.format ? c.format(row, lang) : row[c.field]}</td>
                ))}
                {deact && (
                  <td>
                    <span className={"pt-status-pill " + (isInactive(row, deact) ? "pt-status-removed" : "pt-status-active")}>
                      {translate(G, isInactive(row, deact) ? "inactive" : "active", lang)}
                    </span>
                  </td>
                )}
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td className="pt-empty" colSpan={descriptor.columns.length + (hasBulk ? 1 : 0) + (deact ? 1 : 0)}>
                {translate(G, "empty", lang)}
              </td></tr>
            )}
          </tbody>
          </table>
        </div>

        {pages > 1 && (
          <ul className="pagination">
            <li className={page <= 1 ? "disabled" : ""}>
              <a onClick={() => this.goPage(page - 1)}>‹</a>
            </li>
            <li className="active">
              <a>{page} / {pages}</a>
            </li>
            <li className={page >= pages ? "disabled" : ""}>
              <a onClick={() => this.goPage(page + 1)}>›</a>
            </li>
          </ul>
        )}
      </div>
    );
  }
}
